import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { ReconciliationSupplierOrder } from './types';

const getEndpoint = (supplierId: number) => `ums/v1/cms/reconciliation/suppliers/${supplierId}/orders`;

type TData = ReconciliationSupplierOrder[];
type TError = AxiosError;
type TQueryKey = ['reconciliation-supplier-orders', string, number | null, number | null];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

/** Month's GPO orders for a supplier, optionally scoped to one restaurant (unpaginated, bounded set).
 * `supplierId: null` keeps the query dormant — same lazy-expansion contract as the details hook. */
export function useGetReconciliationSupplierOrdersApi(
  month: string,
  supplierId: number | null,
  restaurantId?: number | null,
  options?: Options
): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async () => {
    try {
      const { data } = await http.get(getEndpoint(supplierId as number), {
        params: { month, ...(restaurantId ? { restaurantId } : {}) },
      });
      // This endpoint wraps the list in { data: [...] } (unlike the bare detail response).
      return data?.data ?? [];
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(['reconciliation-supplier-orders', month, supplierId, restaurantId ?? null], queryFn, {
    enabled: isLoggedIn && !!month && !!supplierId,
    ...options,
  });
}
